import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queryKeys'
import { uuidSchema } from '@/shared/api/schemas'

export const OVERRIDE_SUBJECTS = ['NODE', 'USER'] as const
export const OVERRIDE_MODES = ['ALLOW', 'DENY'] as const
export type OverrideSubject = (typeof OVERRIDE_SUBJECTS)[number]
export type OverrideMode = (typeof OVERRIDE_MODES)[number]

const overrideSchema = z.object({
  subjectType: z.enum(OVERRIDE_SUBJECTS),
  subjectId: uuidSchema,
  mode: z.enum(OVERRIDE_MODES),
})

const planAccessSchema = z.object({
  planId: uuidSchema,
  branchIds: z.array(uuidSchema),
  overrides: z.array(overrideSchema),
})

export type PlanAccessOverride = z.infer<typeof overrideSchema>
export type PlanAccess = z.infer<typeof planAccessSchema>

/** paket → filiallar; ro'yxatda yo'q paket hamma filialda sotiladi (ADR-047) */
export async function fetchPlanBranchMap(signal?: AbortSignal): Promise<Record<string, string[]>> {
  const response = await api.get<unknown>('/api/catalog/plans/branch-map', { ...(signal ? { signal } : {}) })
  return z.record(z.string(), z.array(uuidSchema)).parse(response.data)
}

export function usePlanBranchMap(enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalog.planBranchMap,
    queryFn: ({ signal }) => fetchPlanBranchMap(signal),
    enabled,
  })
}

export async function fetchPlanAccess(planId: string, signal?: AbortSignal): Promise<PlanAccess> {
  const response = await api.get<unknown>(`/api/catalog/plans/${planId}/access`, { ...(signal ? { signal } : {}) })
  return planAccessSchema.parse(response.data)
}

export async function setPlanBranches(planId: string, branchIds: readonly string[]): Promise<PlanAccess> {
  const response = await api.put<unknown>(`/api/catalog/plans/${planId}/branches`, { branchIds })
  return planAccessSchema.parse(response.data)
}

export async function setPlanOverrides(planId: string, overrides: readonly PlanAccessOverride[]): Promise<PlanAccess> {
  const response = await api.put<unknown>(`/api/catalog/plans/${planId}/access-overrides`, { overrides })
  return planAccessSchema.parse(response.data)
}
